<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">客户管理</h1>
            <p class="list-page-description">管理客户信息、跟进记录和客户资料</p>
          </div>
          <div class="list-page-heading-actions">
            <router-link
              v-permission="'referrer:list'"
              to="/referrer"
              class="btn-outline-primary"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              引入人管理
            </router-link>
          </div>
        </div>
      </div>
    </template>

    <!-- 标签页和搜索栏 -->
    <div class="list-page-table-panel mb-6">
      <PageFilterBar
        body-class="p-6"
        :show-refresh="true"
        @refresh="loadCustomers"
      >
            <div class="relative flex-1 min-w-[200px] max-w-sm">
              <input
                v-model="searchKeyword"
                type="text"
                placeholder="搜索客户名称、手机号、企业名称"
                class="form-search-input form-control-sm"
              >
              <svg class="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </div>
            <div class="relative w-36">
              <input
                v-model="tagKeyword"
                type="text"
                placeholder="按标签搜索"
                class="form-input form-control-sm w-full"
              >
            </div>
            <select v-model="selectedSource" class="form-select-native form-control-sm w-32">
              <option value="">全部来源</option>
              <option
                v-for="source in customerSourceOptions"
                :key="source"
                :value="source"
              >
                {{ source }}
              </option>
            </select>
            <select v-model="selectedLevel" class="form-select-native form-control-sm w-32">
              <option value="">全部等级</option>
              <option value="VIP">VIP</option>
              <option value="高级">高级</option>
              <option value="普通">普通</option>
            </select>
            <div class="w-44">
              <el-select
                v-model="selectedSalespersonId"
                filterable
                remote
                clearable
                reserve-keyword
                placeholder="选择销售人员"
                :remote-method="handleSalespersonSearch"
                :loading="salespersonLoading"
                class="w-full form-select form-control-sm"
                popper-class="salesperson-select-dropdown"
                @visible-change="handleSalespersonDropdownVisible"
              >
                <el-option
                  v-for="item in salespersonList"
                  :key="item.id"
                  :label="item.displayName || item.username"
                  :value="item.id"
                >
                  <div class="flex items-center justify-between">
                    <span>{{ item.displayName || item.username }}</span>
                    <span v-if="item.phone" class="text-xs text-slate-400 ml-2">{{ item.phone }}</span>
                  </div>
                </el-option>
                <template v-if="salespersonPagination.total > salespersonList.length" #footer>
                  <div class="text-center py-2 border-t border-slate-200">
                    <button
                      type="button"
                      class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                      :disabled="salespersonLoading"
                      @click.stop="loadMoreSalespersons"
                    >
                      {{ salespersonLoading ? '加载中...' : `加载更多 (${salespersonList.length}/${salespersonPagination.total})` }}
                    </button>
                  </div>
                </template>
              </el-select>
            </div>

        <template #searchActions>
          <button
            @click="handleSearch"
            class="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            搜索
          </button>
          <button
            @click="handleReset"
            class="btn-secondary"
          >
            重置
          </button>
        </template>
      </PageFilterBar>

      <!-- 客户表格 -->
      <div class="px-6 pt-1 pb-4">
        <button
          v-permission="'customer:add'"
          @click="openCreateModal()"
          class="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          新建客户
        </button>
      </div>
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table">
            <thead class="data-table-head">
            <tr>
              <th class="data-table-th text-left">客户企业</th>
              <th class="data-table-th text-left">客户信息</th>
              <th class="data-table-th text-left">手机号</th>
              <th class="data-table-th text-left">引入人</th>
              <th class="data-table-th text-left">规划师</th>
              <th class="data-table-th text-left">客户标签</th>
              <th class="data-table-th is-right">累计方案</th>
              <th class="data-table-th is-right">累计订单</th>
              <th class="data-table-th is-right">累计消费</th>
              <th class="data-table-th is-center">最后跟进</th>
              <th class="data-table-th is-center">操作</th>
            </tr>
            </thead>
            <tbody class="data-table-body">
            <tr v-for="customer in filteredCustomers" :key="customer.id" class="data-table-row cursor-pointer" @click="openDetailModal(customer)">
              <td class="data-table-td">
                <div v-if="customer.companies && customer.companies.length" class="text-sm text-slate-900">
                  <div v-for="company in customer.companies" :key="company.id" class="truncate">
                    {{ company.companyName }}
                  </div>
                </div>
                <span v-else class="text-xs text-slate-400">-</span>
              </td>
              <td class="data-table-td">
                <div class="flex items-center gap-3">
                  <div :class="['w-10 h-10 rounded-full flex items-center justify-center', customer.type === '企业' ? 'bg-purple-100' : 'bg-green-100']">
                    <svg v-if="customer.type === '企业'" class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                    </svg>
                    <svg v-else class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="data-table-cell-title">{{ customer.name }}</div>
                    <div class="data-table-cell-meta">{{ customer.source }}</div>
                  </div>
                </div>
              </td>
              <td class="data-table-td text-sm text-slate-600">{{ customer.phone }}</td>
              <td class="data-table-td text-sm">
                <div class="flex flex-col items-start gap-1.5">
                  <span
                    v-if="customer.referrerName && canViewReferrer"
                    class="text-primary-600 hover:text-primary-700 cursor-pointer underline underline-offset-2"
                    @click.stop="openReferrerDetail(customer)"
                  >
                    {{ customer.referrerName }}
                  </span>
                  <span v-else-if="customer.referrerName" class="text-slate-600">
                    {{ customer.referrerName }}
                  </span>
                  <button
                    v-else
                    v-permission="'customer:add'"
                    class="inline-flex h-6 items-center rounded border border-primary-200 bg-primary-50 px-2 text-xs font-medium text-primary-600 transition-colors hover:border-primary-300 hover:bg-primary-100 hover:text-primary-700"
                    title="绑定引入人"
                    @click.stop="openBindReferrerModal(customer)"
                  >
                    绑定
                  </button>
                  <button
                    v-if="shouldShowApprovalInReferrerColumn(customer)"
                    type="button"
                    :class="['status-badge text-xs', getPendingApprovalStatusClass(customer.pendingApprovalStatus)]"
                    title="查看审批详情"
                    @click.stop="openApprovalDetailModal(customer)"
                  >
                    {{ getPendingApprovalLabel(customer) }}
                  </button>
                </div>
              </td>
              <td class="data-table-td text-sm">
                <div class="flex flex-col items-start gap-1.5">
                  <span v-if="customer.ownerUser?.displayName || customer.ownerUserName">
                    {{ customer.ownerUser?.displayName || customer.ownerUserName }}
                  </span>
                  <span v-else class="text-slate-400">-</span>
                  <button
                    v-if="shouldShowApprovalInOwnerColumn(customer)"
                    type="button"
                    :class="['status-badge text-xs', getPendingApprovalStatusClass(customer.pendingApprovalStatus)]"
                    title="查看审批详情"
                    @click.stop="openApprovalDetailModal(customer)"
                  >
                    {{ getPendingApprovalLabel(customer) }}
                  </button>
                </div>
              </td>
              <td class="data-table-td">
                <div v-if="customer.tags && customer.tags.length" class="flex flex-wrap gap-1.5">
                  <span
                    v-for="tag in customer.tags"
                    :key="tag"
                    :class="[
                      'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs',
                      getTagClass(tag)
                    ]"
                  >
                    {{ tag }}
                  </span>
                </div>
                <span v-else class="text-xs text-slate-400">-</span>
              </td>
              <td class="data-table-td is-right text-sm text-slate-900 font-medium">{{ customer.totalProposals || 0 }}</td>
              <td class="data-table-td is-right text-sm text-slate-900 font-medium">{{ customer.totalOrders }}</td>
              <td class="data-table-td is-right text-sm text-slate-900 font-medium">¥{{ (customer.totalSpent || 0).toLocaleString() }}</td>
              <td class="data-table-td is-center text-sm text-slate-600">{{ customer.lastFollowup || '-' }}</td>
              <td class="data-table-td" @click.stop>
                <div class="flex items-center justify-center gap-1">
                  <button @click="openDetailModal(customer)" class="table-action-button is-primary" title="查看详情">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </button>
                  <button v-permission="'customer:edit'" @click="openEditModal(customer)" class="table-action-button is-primary" title="编辑">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 分页 -->
      <AppPagination
        v-if="pagination.total > 0"
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="[10, 20, 50]"
        mode="simple"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <!-- 新建/编辑客户弹窗 -->
    <div v-if="showCreateModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeCreateModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">{{ isEdit ? '编辑客户' : '新建客户' }}</h3>
            <button @click="closeCreateModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="overflow-y-auto max-h-[calc(90vh-140px)]">
            <!-- 基本信息 -->
            <div class="p-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">客户姓名 <span class="text-red-500">*</span></label>
                  <input v-model="customerForm.name" type="text" class="form-input w-full" placeholder="请输入客户姓名">
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">性别</label>
                  <select v-model="customerForm.gender" class="form-select-native w-full">
                    <option value="">请选择</option>
                    <option value="男">男</option>
                    <option value="女">女</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">联系电话 <span class="text-red-500">*</span></label>
                  <input v-model="customerForm.phone" type="tel" class="form-input w-full">
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">备用电话</label>
                  <input v-model="customerForm.altPhone" type="tel" class="form-input w-full">
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">身份证号</label>
                  <input v-model="customerForm.idCard" type="text" maxlength="18" class="form-input w-full" placeholder="请输入身份证号码">
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">客户邮箱</label>
                  <input v-model="customerForm.email" type="email" class="form-input w-full" placeholder="请输入邮箱地址">
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">客户来源</label>
                  <select v-model="customerForm.source" class="form-select-native w-full">
                    <option
                      v-for="source in customerSourceOptions"
                      :key="source"
                      :value="source"
                    >
                      {{ source }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">引入人</label>
                  <div class="flex gap-2">
                    <div class="flex-1">
                      <el-autocomplete
                        v-model="referrerSearchKeyword"
                        :fetch-suggestions="queryReferrerSearch"
                        placeholder="搜索并选择引入人"
                        class="custom-autocomplete w-full"
                        :trigger-on-focus="true"
                        clearable
                        @focus="onReferrerFocus"
                        @clear="handleReferrerClear"
                        @select="onReferrerSelect"
                      >
                        <template #prefix>
                          <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                          </svg>
                        </template>
                        <template #default="{ item }">
                          <div class="referrer-item">
                            <div class="referrer-avatar">
                              <span>{{ item.name.charAt(0) }}</span>
                            </div>
                            <div class="referrer-info">
                              <div class="referrer-name">{{ item.name }}</div>
                              <div class="referrer-phone">{{ item.phone }}</div>
                            </div>
                            <div class="referrer-count">
                              引入 {{ item.referralCount || 0 }} 人
                            </div>
                          </div>
                        </template>
                      </el-autocomplete>
                    </div>
                    <button
                      type="button"
                      @click="showReferrerModal = true"
                      class="btn-outline-primary"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                      </svg>
                      新增
                    </button>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">客户状态</label>
                  <select v-model="customerForm.status" class="form-select-native w-full">
                    <option :value="0">潜在</option>
                    <option :value="1">跟进中</option>
                    <option :value="2">正式</option>
                    <option :value="3">已流失</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">省份/城市</label>
                  <el-cascader
                    v-model="areaCodes"
                    :options="regionTree"
                    placeholder="请选择省市区"
                    filterable
                    clearable
                    class="w-full"
                    @change="handleAreaChange"
                  />
                </div>
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-700 mb-2">详细住址</label>
                  <input v-model="customerForm.address" type="text" class="form-input w-full" placeholder="请输入详细住址">
                </div>
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-700 mb-2">备注信息</label>
                  <textarea v-model="customerForm.remark" rows="3" class="form-textarea" placeholder="请输入备注信息"></textarea>
                </div>
                <div class="md:col-span-2">
                  <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div class="flex items-center justify-between mb-3">
                      <label class="text-sm font-medium text-slate-700">客户标签</label>
                      <span class="text-xs text-slate-500">逐个添加，使用英文逗号分隔</span>
                    </div>
                    <div class="flex gap-2 mb-3">
                      <input
                        v-model="newTag"
                        type="text"
                        class="form-input flex-1 bg-white"
                        placeholder="输入标签后点击添加"
                        @keyup.enter="addTag"
                      >
                      <button
                        type="button"
                        @click="addTag"
                        class="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        添加
                      </button>
                    </div>
                    <div v-if="customerForm.tags.length" class="flex flex-wrap gap-2 mb-3">
                      <span
                        v-for="tag in customerForm.tags"
                        :key="tag"
                        :class="[
                      'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs',
                      getTagClass(tag)
                    ]"
                      >
                        {{ tag }}
                        <button type="button" class="text-primary-500 hover:text-primary-700" @click="toggleTag(tag)">x</button>
                      </span>
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <button
                        v-for="tag in availableTags"
                        :key="tag"
                        @click="toggleTag(tag)"
                        :class="[
                          'px-3 py-1.5 rounded-full text-xs transition-colors border',
                          customerForm.tags.includes(tag)
                            ? 'bg-primary-100 text-primary-700 border-primary-300'
                            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                        ]"
                      >
                        {{ tag }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 企业信息 -->
            <div v-if="false" class="p-6">
              <div v-if="customerForm.type === '企业'" class="space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">企业名称</label>
                    <input v-model="customerForm.companyName" type="text" class="form-input w-full" placeholder="请输入企业名称">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">社会统一信用代码</label>
                    <input v-model="customerForm.creditCode" type="text" class="form-input w-full" placeholder="请输入社会统一信用代码">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">法人代表</label>
                    <input v-model="customerForm.legalPerson" type="text" class="form-input w-full" placeholder="请输入法人代表姓名">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">企业性质</label>
                    <select v-model="customerForm.companyNature" class="form-select-native w-full">
                      <option value="">请选择</option>
                      <option value="国有企业">国有企业</option>
                      <option value="民营企业">民营企业</option>
                      <option value="外资企业">外资企业</option>
                      <option value="合资企业">合资企业</option>
                      <option value="股份制企业">股份制企业</option>
                      <option value="个体工商户">个体工商户</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">注册资本</label>
                    <input v-model="customerForm.registeredCapital" type="text" class="form-input w-full" placeholder="如：1000万">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">成立日期</label>
                    <el-date-picker v-model="customerForm.establishedDate" type="date" class="w-full form-date" format="YYYY年MM月DD日" value-format="YYYY-MM-DD" placeholder="请选择成立日期" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">认证日期</label>
                    <el-date-picker v-model="customerForm.certificationDate" type="date" class="w-full form-date" format="YYYY年MM月DD日" value-format="YYYY-MM-DD" placeholder="请选择认证日期" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">企业经营地址</label>
                    <input v-model="customerForm.companyAddress" type="text" class="form-input w-full" placeholder="请输入企业经营地址">
                  </div>
                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-slate-700 mb-2">经营范围</label>
                    <textarea v-model="customerForm.businessScope" rows="2" class="form-textarea" placeholder="请输入经营范围"></textarea>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-12 text-slate-500">
                <svg class="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                <p>个人客户无需填写企业信息</p>
              </div>
            </div>

        

            <!-- 标签设置 -->
            <div v-if="false" class="p-6">
              <div class="mb-6">
                <h4 class="text-sm font-medium text-slate-700 mb-3">客户标签</h4>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="tag in availableTags"
                    :key="tag"
                    @click="toggleTag(tag)"
                    :class="[
                      'px-3 py-1.5 rounded-full text-sm transition-colors border',
                      customerForm.tags.includes(tag)
                        ? 'bg-primary-100 text-primary-700 border-primary-300'
                        : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                    ]"
                  >
                    {{ tag }}
                  </button>
                </div>
              </div>
              <div>
                <h4 class="text-sm font-medium text-slate-700 mb-3">备注信息</h4>
                <textarea v-model="customerForm.remark" rows="4" class="form-textarea" placeholder="请输入备注信息"></textarea>
              </div>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeCreateModal" class="btn-secondary">取消</button>
            <button
              :disabled="isSubmitting"
              @click="handleSubmitCustomer"
              class="btn-primary"
            >
              {{ isEdit ? '保存修改' : '创建客户' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 客户详情弹窗组件 -->
    <CustomerDetailModal
      v-model="showDetailModal"
      :customer="selectedCustomer"
      @open-add-enterprise="openAddEnterpriseModal"
      @open-edit-enterprise="openEditEnterpriseModal"
      @edit-customer="handleEditCustomerFromDetail"
      @delete-company="handleDeleteCompany"
      @refresh="loadCustomers"
    />

    <!-- 引入人详情弹窗 -->
    <ReferrerDetailModal
      v-model:visible="showReferrerDetailModal"
      :referrer-id="selectedReferrerId"
      :referrer-info="selectedReferrerInfo"
    />

    <!-- 添加/编辑企业弹窗 -->
    <div v-if="showCompanyModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeCompanyModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">{{ isCompanyEdit ? '编辑企业' : '新增企业' }}</h3>
            <button @click="closeCompanyModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="md:col-span-2">
                <div class="flex items-center justify-between gap-3 mb-2">
                  <label class="block text-sm font-medium text-slate-700">先上传营业执照自动识别</label>
                </div>
                <div class="rounded-lg border border-primary-200 bg-primary-50/60 p-4">
                  <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div class="text-sm font-medium text-slate-800">
                        {{ companyForm.licenseUrl ? '已上传营业执照，可重新上传识别' : '上传图片后自动回填下方企业信息' }}
                      </div>
                      <div class="mt-1 text-xs text-slate-500">支持 JPG、PNG，图片大小不超过 7MB。</div>
                    </div>
                    <button
                      type="button"
                      class="btn-outline-primary inline-flex items-center gap-2 justify-center"
                      :disabled="isLicenseOcrLoading"
                      @click="triggerLicenseUpload"
                    >
                      <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 16V4m0 0l-4 4m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"/>
                      </svg>
                      {{ isLicenseOcrLoading ? '识别中...' : (companyForm.licenseUrl ? '重新上传识别' : '上传并识别') }}
                    </button>
                    <input
                      ref="licenseFileInputRef"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      class="hidden"
                      @change="handleLicenseFileChange"
                    >
                  </div>
                  <div v-if="companyForm.licensePreviewUrl || companyForm.licenseUrl" class="mt-3 flex items-start gap-3">
                    <img
                      :src="companyForm.licensePreviewUrl || companyForm.licenseUrl"
                      alt="营业执照"
                      class="h-20 w-32 rounded border border-slate-200 bg-white object-cover"
                    >
                    <div class="text-xs text-slate-500 leading-5">
                      识别结果会回填公司名称、统一社会信用代码、法定代表人、注册资本、成立日期、注册地址和经营范围。
                    </div>
                  </div>
                </div>
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-700 mb-2">公司名称 <span class="text-red-500">*</span></label>
                <input v-model="companyForm.companyName" type="text" class="form-input w-full" placeholder="请输入公司名称">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">统一社会信用代码</label>
                <input v-model="companyForm.socialCreditCode" type="text" class="form-input w-full" placeholder="请输入统一社会信用代码">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">法定代表人</label>
                <input v-model="companyForm.legalPerson" type="text" class="form-input w-full" placeholder="请输入法定代表人">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">注册资本</label>
                <input v-model="companyForm.registeredCapital" type="text" class="form-input w-full" placeholder="如：500万">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">成立日期</label>
                <el-date-picker v-model="companyForm.establishmentDate" type="date" class="w-full form-date" format="YYYY年MM月DD日" value-format="YYYY-MM-DD" placeholder="请选择成立日期" />
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-700 mb-2">注册地址</label>
                <input v-model="companyForm.registeredAddress" type="text" class="form-input w-full" placeholder="请输入注册地址">
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-700 mb-2">经营范围</label>
                <textarea v-model="companyForm.businessScope" rows="3" class="form-textarea" placeholder="请输入经营范围"></textarea>
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-700 mb-2">备注</label>
                <textarea v-model="companyForm.remark" rows="2" class="form-textarea" placeholder="请输入备注"></textarea>
              </div>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeCompanyModal" class="btn-secondary">取消</button>
            <button
              @click="handleSubmitCompany"
              class="btn-primary"
              :disabled="isCompanySubmitting"
            >
              {{ isCompanySubmitting ? '提交中...' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 删除企业确认弹窗 -->
    <div v-if="showDeleteCompanyModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeDeleteCompanyModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">确认删除</h3>
            <button @click="closeDeleteCompanyModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6 text-sm text-slate-600">
            确认删除企业「{{ companyToDelete?.name || companyToDelete?.companyName || '-' }}」吗？此操作不可撤销。
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeDeleteCompanyModal" class="btn-secondary">取消</button>
            <button @click="confirmDeleteCompany" class="btn-danger-secondary">删除</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增引入人弹窗 -->
    <ReferrerFormModal
      v-model:visible="showReferrerModal"
      @success="handleReferrerSuccess"
    />

    <ApprovalDetailModal
      v-model="showApprovalDetailModal"
      :instance-id="selectedApprovalInstanceId"
      type="customer"
      title="审批详情"
    />

    <!-- 绑定引入人弹窗 -->
    <div v-if="showBindReferrerModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeBindReferrerModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-lg w-full">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">绑定引入人</h3>
            <button @click="closeBindReferrerModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6 space-y-4">
            <div>
              <div class="text-sm text-slate-500 mb-1">客户</div>
              <div class="font-medium text-slate-900">{{ referrerBindingCustomer?.name || '-' }}</div>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">引入人 <span class="text-red-500">*</span></label>
              <div class="flex gap-2">
                <div class="flex-1">
                  <el-autocomplete
                    v-model="referrerSearchKeyword"
                    :fetch-suggestions="queryReferrerSearch"
                    placeholder="搜索并选择引入人"
                    class="custom-autocomplete w-full"
                    :trigger-on-focus="true"
                    clearable
                    @clear="handleReferrerClear"
                    @focus="onReferrerFocus"
                    @select="onReferrerSelect"
                  >
                    <template #prefix>
                      <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                    </template>
                    <template #default="{ item }">
                      <div class="referrer-item">
                        <div class="referrer-avatar">
                          <span>{{ item.name.charAt(0) }}</span>
                        </div>
                        <div class="referrer-info">
                          <div class="referrer-name">{{ item.name }}</div>
                          <div class="referrer-phone">{{ item.phone }}</div>
                        </div>
                        <div class="referrer-count">
                          引入 {{ item.referralCount || 0 }} 人
                        </div>
                      </div>
                    </template>
                  </el-autocomplete>
                </div>
                <button
                  type="button"
                  @click="showReferrerModal = true"
                  class="btn-outline-primary"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  新增
                </button>
              </div>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeBindReferrerModal" class="btn-secondary">取消</button>
            <button
              @click="handleBindReferrer"
              class="btn-primary"
              :disabled="isBindingReferrer"
            >
              {{ isBindingReferrer ? '提交中...' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import AppPagination from '@/components/AppPagination.vue'
import ApprovalDetailModal from '@/components/ApprovalDetailModal.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import CustomerDetailModal from '@/components/CustomerDetailModal.vue'
import ReferrerDetailModal from '@/views/referrer/components/ReferrerDetailModal.vue'
import ReferrerFormModal from '@/components/ReferrerFormModal.vue'
import chinaAreaData from 'china-area-data'
// chinaAreaData['86'] 包含省级数据，CodeToText 用于code转中文
const provinceData = chinaAreaData['86']
const CodeToText = {}
// 构建 CodeToText 映射：code -> name
Object.entries(chinaAreaData).forEach(([code, data]) => {
  if (code !== '86' && typeof data === 'object') {
    Object.entries(data).forEach(([subCode, name]) => {
      CodeToText[subCode] = name
    })
  }
})
import { getReferrerById, getReferrerPage } from '@/api/referrer'
import {
  createCustomer,
  updateCustomer,
  bindCustomerReferrer,
  getCustomer,
  getCustomerPage,
  createCompany,
  updateCompany,
  recognizeCompanyLicense,
  getCompanyPage,
  deleteCompany,
  getCustomerMaterialTypes,
  getCustomerMaterials
} from '@/api/customer'
import { getUserList } from '@/api/user'
import { uploadFile } from '@/api/file'

// Tab state
const activeDetailTab = ref('basic')

// Modal state
const showCreateModal = ref(false)
const showDetailModal = ref(false)
const showCompanyModal = ref(false)
const showDeleteCompanyModal = ref(false)
const isEdit = ref(false)
const isCompanyEdit = ref(false)
const selectedCustomer = ref(null)
const companyToDelete = ref(null)
const showReferrerDetailModal = ref(false)
const selectedReferrerId = ref(null)
const selectedReferrerInfo = ref(null)
const showBindReferrerModal = ref(false)
const referrerBindingCustomer = ref(null)
const isBindingReferrer = ref(false)
const showApprovalDetailModal = ref(false)
const selectedApprovalInstanceId = ref(null)

// Referrer state
const referrerList = ref([])
const referrerLoading = ref(false)
const referrerSearchKeyword = ref('')

// Salesperson state
const salespersonList = ref([])
const salespersonLoading = ref(false)
const salespersonSearchKeyword = ref('')
const salespersonPagination = reactive({
  page: 1,
  size: 20,
  total: 0
})

const userStore = useUserStore()
const canViewReferrer = computed(() => userStore.hasPermission('referrer:list'))

// Filter state
const showReferrerModal = ref(false)
const searchKeyword = ref('')
const selectedSource = ref('')
const selectedLevel = ref('')
const selectedSalespersonId = ref(null)
const tagKeyword = ref('')
const newTag = ref('')
const isSubmitting = ref(false)
const isCompanyFetching = ref(false)
const isCompanySubmitting = ref(false)
const isLicenseOcrLoading = ref(false)
const licenseFileInputRef = ref(null)
const isMaterialTypesLoading = ref(false)
const isMaterialListLoading = ref(false)
const materialTypeCategories = ref([])
const customerMaterials = ref([])
const pagination = reactive({
  page: 1,
  size: 10,
  total: 0,
  pages: 0
})

// 将扁平的地区数据转换为树形结构（用于级联选择器）
const regionTree = computed(() => {
  if (!provinceData) return []
  const result = []
  for (const [provinceCode, provinceName] of Object.entries(provinceData)) {
    const province = { value: provinceCode, label: provinceName, children: [] }
    const cityData = chinaAreaData[provinceCode]
    if (cityData) {
      for (const [cityCode, cityName] of Object.entries(cityData)) {
        const city = { value: cityCode, label: cityName, children: [] }
        const districtData = chinaAreaData[cityCode]
        if (districtData) {
          for (const [districtCode, districtName] of Object.entries(districtData)) {
            city.children.push({ value: districtCode, label: districtName })
          }
        }
        province.children.push(city)
      }
    }
    result.push(province)
  }
  return result
})

// 省市区选择器
const areaCodes = ref([])
const handleAreaChange = (value) => {
  if (value && value.length >= 2) {
    const province = provinceData[value[0]] || ''
    const city = CodeToText[value[1]] || ''
    customerForm.province = province
    customerForm.city = city
  } else {
    customerForm.province = ''
    customerForm.city = ''
    areaCodes.value = []
  }
}

const customerSourceOptions = ['小红书', '政府网站', '客户转介绍', '渠道介绍', '员工自拓']
const defaultCustomerSource = customerSourceOptions[0]
const availableTags = ['高价值', '复购客户', '投诉客户', 'VIP推荐', '新客户', '意向客户']

// Customer form
const customerForm = reactive({
  id: null,
  name: '',
  type: '个人',
  contact: '',
  gender: '',
  phone: '',
  altPhone: '',
  idCard: '',
  email: '',
  source: defaultCustomerSource,
  referrerId: null,
  level: '普通',
  status: 0,
  province: '',
  city: '',
  address: '',
  tags: [],
  remark: '',
  contacts: [{ name: '', phone: '', role: '' }],
  // Enterprise fields
  companyName: '',
  creditCode: '',
  legalPerson: '',
  companyNature: '',
  registeredCapital: '',
  establishedDate: '',
  certificationDate: '',
  companyAddress: '',
  businessScope: ''
})

const companyForm = reactive({
  id: null,
  companyName: '',
  socialCreditCode: '',
  legalPerson: '',
  registeredCapital: '',
  establishmentDate: '',
  registeredAddress: '',
  businessScope: '',
  licenseUrl: '',
  licensePreviewUrl: '',
  remark: ''
})

const companyPagination = reactive({
  page: 1,
  size: 20,
  total: 0,
  pages: 0
})

const levelValueMap = { 普通: 0, 高级: 1, 重要: 1, VIP: 2 }
const levelLabelMap = { 0: '普通', 1: '高级', 2: 'VIP' }
const genderValueMap = { 男: 0, 女: 1, 未知: 2 }
const genderLabelMap = { 0: '男', 1: '女', 2: '未知' }

const fetchReferrers = async () => {
  referrerLoading.value = true
  try {
    const result = await getReferrerPage({ page: 1, size: 100 })
    referrerList.value = result?.records || []
  } catch (error) {
    console.error('获取引入人列表失败:', error)
  } finally {
    referrerLoading.value = false
  }
}

// 引入人搜索方法 - el-autocomplete 使用
const queryReferrerSearch = (queryString, cb) => {
  // 如果列表为空，先加载数据
  if (referrerList.value.length === 0) {
    fetchReferrers().then(() => {
      filterReferrers(queryString, cb)
    })
  } else {
    filterReferrers(queryString, cb)
  }
}

const filterReferrers = (queryString, cb) => {
  const results = referrerList.value.filter(item => {
    if (!queryString) return true
    const keyword = queryString.toLowerCase()
    return item.name.toLowerCase().includes(keyword) ||
           item.phone.includes(keyword)
  })
  cb(results)
}

const handleReferrerSuccess = () => {
  fetchReferrers()
}

// 销售人员相关方法
const fetchSalespersons = async (keyword = '', isLoadMore = false) => {
  if (salespersonLoading.value) return

  salespersonLoading.value = true
  try {
    const params = {
      page: isLoadMore ? salespersonPagination.page + 1 : 1,
      size: salespersonPagination.size,
      status: 0 // 只查询正常状态的用户
    }
    if (keyword) {
      params.username = keyword
    }

    const result = await getUserList(params)

    if (isLoadMore) {
      salespersonList.value = [...salespersonList.value, ...(result?.records || [])]
      salespersonPagination.page = result?.current || salespersonPagination.page + 1
    } else {
      salespersonList.value = result?.records || []
      salespersonPagination.page = result?.current || 1
    }

    salespersonPagination.total = result?.total || 0
  } catch (error) {
    console.error('获取销售人员列表失败:', error)
    ElMessage.error('获取销售人员列表失败')
  } finally {
    salespersonLoading.value = false
  }
}

let salespersonSearchTimer = null
const handleSalespersonSearch = (query) => {
  salespersonSearchKeyword.value = query
  // 添加防抖，避免频繁请求
  if (salespersonSearchTimer) {
    clearTimeout(salespersonSearchTimer)
  }
  salespersonSearchTimer = setTimeout(() => {
    fetchSalespersons(query, false)
  }, 300)
}

const handleSalespersonDropdownVisible = (visible) => {
  if (visible && salespersonList.value.length === 0) {
    fetchSalespersons('', false)
  }
}

const loadMoreSalespersons = () => {
  if (!salespersonLoading.value) {
    fetchSalespersons(salespersonSearchKeyword.value, true)
  }
}

// 获得焦点时加载数据
const onReferrerFocus = () => {
  if (referrerList.value.length === 0) {
    fetchReferrers()
  }
}

const onReferrerSelect = (item) => {
  if (item && item.id) {
    customerForm.referrerId = item.id
    referrerSearchKeyword.value = item.name
  }
}

const handleReferrerClear = () => {
  customerForm.referrerId = null
  referrerSearchKeyword.value = ''
}

const syncReferrerDisplayToForm = (data = {}) => {
  const referrerId = data.referrerId || null
  const referrerName = data.referrerName || data.referrer?.name || ''
  customerForm.referrerId = referrerId
  referrerSearchKeyword.value = referrerId ? referrerName : ''
}

const fillReferrerToForm = (referrer) => {
  if (!referrer?.id) {
    handleReferrerClear()
    return
  }
  customerForm.referrerId = referrer.id
  referrerSearchKeyword.value = referrer.phone ? `${referrer.name} ${referrer.phone}` : referrer.name
}

const fetchReferrerById = async (id) => {
  if (!id) return undefined
  try {
    return await getReferrerById(id)
  } catch (error) {
    console.error('获取引入人信息失败:', error)
    return undefined
  }
}

const normalizeLevelLabel = (levelName, level) => {
  if (levelName) {
    return levelName === '重要' ? '高级' : levelName
  }
  return levelLabelMap[level] || ''
}

const mapCustomerFromApi = (data = {}) => {
  const type = data.companies && data.companies.length ? '企业' : '个人'
  const levelLabel = normalizeLevelLabel(data.levelName, data.level)
  return {
    id: data.id,
    customerNo: data.customerNo,
    name: data.name || '',
    gender: genderLabelMap[data.gender] || '',
    phone: data.phone || '',
    altPhone: data.telPhone || '',
    email: data.email || '',
    idCard: data.idCard || '',
    source: data.source || '',
    referrerId: data.referrerId,
    referrerName: data.referrerName || '',
    level: levelLabel,
    levelValue: data.level,
    status: data.status,
    statusName: data.statusName || '',
    ownerUserId: data.ownerUserId,
    ownerUserName: data.ownerUserName || '',
    ownerUser: data.ownerUser || null,
    ownerDeptId: data.ownerDeptId,
    ownerDeptName: data.ownerDeptName || '',
    address: data.address || '',
    detailAddress: data.detailAddress || '',
    remark: data.remark || '',
    tags: Array.isArray(data.tags)
      ? data.tags
      : typeof data.tags === 'string'
        ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
        : [],
    companies: data.companies || [],
    type,
    contact: data.referrerName || data.ownerUserName || data.name || '-',
    isKey: data.level === 2 || data.level === 1,
    totalOrders: data.totalOrders || 0,
    totalProposals: data.totalProposals || 0,
    totalSpent: data.totalSpent || 0,
    lastFollowup: data.lastFollowup || '',
    pendingApprovalStatus: data.pendingApprovalStatus,
    pendingApprovalInstanceId: data.pendingApprovalInstanceId,
    pendingApprovalType: data.pendingApprovalType || '',
    pendingApprovalDesc: data.pendingApprovalDesc || ''
  }
}

const pendingApprovalStatusMap = {
  0: { text: '审批中', class: 'bg-amber-100 text-amber-700' },
  1: { text: '已通过', class: 'bg-green-100 text-green-700' },
  2: { text: '已驳回', class: 'bg-red-100 text-red-700' },
  3: { text: '已取消', class: 'bg-slate-100 text-slate-600' }
}

const hasPendingApproval = (item) => item?.pendingApprovalInstanceId != null && item?.pendingApprovalStatus != null
const isApprovalInProgress = (item) => Number(item?.pendingApprovalStatus) === 0 && item?.pendingApprovalInstanceId != null
const getPendingApprovalStatusClass = (status) => pendingApprovalStatusMap[Number(status)]?.class || 'bg-slate-100 text-slate-600'
const isReferrerApproval = (item) => {
  const type = item?.pendingApprovalType || ''
  const desc = item?.pendingApprovalDesc || ''
  return type === 'customer_referrer_change_approval' || desc.includes('引入人')
}
const isCustomerOwnerApproval = (item) => {
  const type = item?.pendingApprovalType || ''
  const desc = item?.pendingApprovalDesc || ''
  return [
    'customer_owner_change_approval',
    'customer_executor_change_approval',
    'customer_assignee_change_approval'
  ].includes(type) ||
    desc.includes('执行人') ||
    desc.includes('负责人') ||
    desc.includes('规划师') ||
    desc.includes('所属销售')
}
const shouldShowApprovalInReferrerColumn = (item) => hasPendingApproval(item) && isReferrerApproval(item)
const shouldShowApprovalInOwnerColumn = (item) => hasPendingApproval(item) && (isCustomerOwnerApproval(item) || !isReferrerApproval(item))
const getPendingApprovalLabel = (item) => {
  const statusText = pendingApprovalStatusMap[Number(item?.pendingApprovalStatus)]?.text || '未知'
  const desc = item?.pendingApprovalDesc || '客户变更审批'
  const prefix = desc.endsWith('审批') ? desc.slice(0, -2) : desc
  return `${prefix}${statusText}`
}

const openApprovalDetailModal = (item) => {
  if (!item?.pendingApprovalInstanceId) {
    ElMessage.warning('暂无审批实例')
    return
  }
  selectedApprovalInstanceId.value = item.pendingApprovalInstanceId
  showApprovalDetailModal.value = true
}

const mapCompanyFromApi = (data = {}) => {
  return {
    id: data.id,
    name: data.companyName || '',
    relation: data.remark || '-',
    status: data.status,
    statusLabel: data.status === 1 ? '停用' : '正常',
    creditCode: data.socialCreditCode || '',
    legalPerson: data.legalPerson || '',
    registeredCapital: data.registeredCapital || '',
    establishedDate: data.establishmentDate || '',
    companyAddress: data.registeredAddress || '',
    businessScope: data.businessScope || '',
    licenseUrl: data.licenseUrl || '',
    licensePreviewUrl: data.licensePreviewUrl || data.licenseUrl || '',
    remark: data.remark || ''
  }
}

const mapCustomerToForm = (data = {}) => {
  Object.assign(customerForm, {
    id: data.id || null,
    name: data.name || '',
    type: data.type || (data.companies && data.companies.length ? '企业' : '个人'),
    contact: data.contact || '',
    gender: genderLabelMap[data.gender] || data.gender || '',
    phone: data.phone || '',
    altPhone: data.telPhone || data.altPhone || '',
    idCard: data.idCard || '',
    email: data.email || '',
    source: data.source || customerForm.source,
    referrerId: data.referrerId || null,
    level: normalizeLevelLabel(data.levelName, data.level) || data.level || '',
    status: data.status ?? 0,
    province: '',
    city: '',
    address: data.detailAddress || data.address || '',
    tags: Array.isArray(data.tags)
      ? data.tags
      : typeof data.tags === 'string'
        ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
        : [],
    remark: data.remark || '',
    contacts: data.contacts || [{ name: '', phone: '', role: '' }],
    companyName: data.companyName || '',
    creditCode: data.creditCode || '',
    legalPerson: data.legalPerson || '',
    companyNature: data.companyNature || '',
    registeredCapital: data.registeredCapital || '',
    establishedDate: data.establishedDate || '',
    certificationDate: data.certificationDate || '',
    companyAddress: data.companyAddress || '',
    businessScope: data.businessScope || ''
  })
  syncReferrerDisplayToForm(data)
}

const buildCustomerPayload = () => {
  const address = [customerForm.province, customerForm.city].filter(Boolean).join('')
  const levelValue = levelValueMap[customerForm.level]
  const genderValue = genderValueMap[customerForm.gender]
  return {
    name: customerForm.name?.trim(),
    gender: genderValue ?? undefined,
    phone: customerForm.phone?.trim(),
    telPhone: customerForm.altPhone?.trim() || undefined,
    email: customerForm.email?.trim() || undefined,
    idCard: customerForm.idCard?.trim() || undefined,
    source: customerForm.source || undefined,
    referrerId: customerForm.referrerId || undefined,
    level: Number.isInteger(levelValue) ? levelValue : undefined,
    status: customerForm.status ?? undefined,
    address: address || undefined,
    detailAddress: customerForm.address?.trim() || undefined,
    remark: customerForm.remark?.trim() || undefined,
    tags: customerForm.tags.length ? customerForm.tags.join(',') : undefined
  }
}

const resetCompanyForm = () => {
  Object.assign(companyForm, {
    id: null,
    companyName: '',
    socialCreditCode: '',
    legalPerson: '',
    registeredCapital: '',
    establishmentDate: '',
    registeredAddress: '',
    businessScope: '',
    licenseUrl: '',
    licensePreviewUrl: '',
    remark: ''
  })
}

const mapCompanyToForm = (data = {}) => {
  Object.assign(companyForm, {
    id: data.id || null,
    companyName: data.companyName || data.name || '',
    socialCreditCode: data.socialCreditCode || data.creditCode || '',
    legalPerson: data.legalPerson || '',
    registeredCapital: data.registeredCapital || '',
    establishmentDate: data.establishmentDate || data.establishedDate || '',
    registeredAddress: data.registeredAddress || data.companyAddress || '',
    businessScope: data.businessScope || '',
    licenseUrl: data.licenseUrl || '',
    licensePreviewUrl: data.licensePreviewUrl || data.licenseUrl || '',
    remark: data.remark || data.relation || ''
  })
}

const buildCompanyPayload = () => {
  return {
    customerId: selectedCustomer.value?.id,
    companyName: companyForm.companyName?.trim(),
    socialCreditCode: companyForm.socialCreditCode?.trim() || undefined,
    legalPerson: companyForm.legalPerson?.trim() || undefined,
    registeredCapital: companyForm.registeredCapital?.trim() || undefined,
    establishmentDate: companyForm.establishmentDate || undefined,
    registeredAddress: companyForm.registeredAddress?.trim() || undefined,
    businessScope: companyForm.businessScope?.trim() || undefined,
    licenseUrl: companyForm.licenseUrl?.trim() || undefined,
    remark: companyForm.remark?.trim() || undefined
  }
}

const applyCompanyLicenseOcrResult = (data = {}) => {
  Object.assign(companyForm, {
    companyName: data.companyName || companyForm.companyName,
    socialCreditCode: data.socialCreditCode || companyForm.socialCreditCode,
    legalPerson: data.legalPerson || companyForm.legalPerson,
    registeredCapital: data.registeredCapital || companyForm.registeredCapital,
    establishmentDate: data.establishmentDate || companyForm.establishmentDate,
    registeredAddress: data.registeredAddress || companyForm.registeredAddress,
    businessScope: data.businessScope || companyForm.businessScope
  })
}

const triggerLicenseUpload = () => {
  if (isLicenseOcrLoading.value) return
  licenseFileInputRef.value?.click()
}

const handleLicenseFileChange = async (event) => {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  if (!/^image\/(png|jpeg|jpg)$/.test(file.type)) {
    ElMessage.warning('请上传 JPG 或 PNG 图片')
    return
  }
  const maxSize = 7 * 1024 * 1024
  if (file.size > maxSize) {
    ElMessage.warning('图片大小不能超过7MB')
    return
  }
  isLicenseOcrLoading.value = true
  try {
    const uploadResult = await uploadFile(file, 'customer-license', 'private')
    const licenseKey = uploadResult?.fileName
    const previewUrl = uploadResult?.url
    if (!licenseKey || !previewUrl) {
      throw new Error('上传结果缺少营业执照文件信息')
    }
    companyForm.licenseUrl = licenseKey
    companyForm.licensePreviewUrl = previewUrl
    const ocrResult = await recognizeCompanyLicense({ imageUrl: previewUrl })
    applyCompanyLicenseOcrResult(ocrResult)
    ElMessage.success('上传并识别完成')
  } catch (error) {
    ElMessage.error(error?.message || '上传或识别失败')
  } finally {
    isLicenseOcrLoading.value = false
  }
}

// Sample data
const customers = ref([])

const relatedEnterprises = ref([])

const fetchCustomerDetail = async (id) => {
  if (!id) return null
  try {
    const result = await getCustomer(id)
    return result
  } catch (error) {
    ElMessage.error(error?.message || '获取客户详情失败')
    return null
  }
}

const loadCompanies = async () => {
  const customerId = selectedCustomer.value?.id
  if (!customerId) return
  isCompanyFetching.value = true
  try {
    const payload = {
      customerId,
      page: companyPagination.page,
      size: companyPagination.size
    }
    const result = await getCompanyPage(payload)
    companyPagination.page = result?.current || companyPagination.page
    companyPagination.size = result?.size || companyPagination.size
    companyPagination.total = result?.total || 0
    companyPagination.pages = result?.pages || 0
    relatedEnterprises.value = (result?.records || []).map(mapCompanyFromApi)
  } catch (error) {
    ElMessage.error(error?.message || '获取企业列表失败')
  } finally {
    isCompanyFetching.value = false
  }
}

const loadMaterialTypes = async () => {
  if (isMaterialTypesLoading.value) return
  isMaterialTypesLoading.value = true
  try {
    const result = await getCustomerMaterialTypes()
    materialTypeCategories.value = Array.isArray(result) ? result : []
  } catch (error) {
    ElMessage.error(error?.message || '获取资料类型失败')
  } finally {
    isMaterialTypesLoading.value = false
  }
}

const loadCustomerMaterials = async () => {
  const customerId = selectedCustomer.value?.id
  if (!customerId) return
  isMaterialListLoading.value = true
  try {
    const result = await getCustomerMaterials(customerId)
    customerMaterials.value = Array.isArray(result) ? result : []
  } catch (error) {
    ElMessage.error(error?.message || '获取客户资料失败')
  } finally {
    isMaterialListLoading.value = false
  }
}

const loadCustomers = async () => {
  try {
    const payload = {
      keyword: searchKeyword.value || undefined,
      level: selectedLevel.value ? levelValueMap[selectedLevel.value] : undefined,
      source: selectedSource.value || undefined,
      tags: tagKeyword.value || undefined,
      ownerUserId: selectedSalespersonId.value || undefined,
      page: pagination.page,
      size: pagination.size
    }
    const result = await getCustomerPage(payload)
    pagination.page = result?.current || pagination.page
    pagination.size = result?.size || pagination.size
    pagination.total = result?.total || 0
    pagination.pages = result?.pages || 0
    customers.value = (result?.records || []).map(mapCustomerFromApi)
  } catch (error) {
    ElMessage.error(error?.message || '获取客户列表失败')
  }
}

const handleSubmitCustomer = async () => {
  if (!customerForm.name || !customerForm.phone) {
    ElMessage.warning('请输入必填项')
    return
  }
  isSubmitting.value = true
  try {
    const payload = buildCustomerPayload()
    if (isEdit.value) {
      const result = await updateCustomer({ ...payload, id: customerForm.id })
      ElMessage.success(isApprovalInProgress(result) ? '已提交引入人变更审批，审批通过后生效' : '更新成功')
    } else {
      await createCustomer(payload)
      ElMessage.success('创建成功')
    }
    showCreateModal.value = false
    await loadCustomers()
  } catch (error) {
    ElMessage.error(error?.message || '操作失败')
  } finally {
    isSubmitting.value = false
  }
}

const handleSubmitCompany = async () => {
  if (!selectedCustomer.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  if (!companyForm.companyName) {
    ElMessage.warning('请输入企业名称')
    return
  }
  isCompanySubmitting.value = true
  try {
    const payload = buildCompanyPayload()
    if (isCompanyEdit.value) {
      await updateCompany({ ...payload, id: companyForm.id })
      ElMessage.success('更新成功')
    } else {
      await createCompany(payload)
      ElMessage.success('创建成功')
    }
    showCompanyModal.value = false
    await loadCompanies()
    // 更新客户对象触发子组件刷新
    const detail = await fetchCustomerDetail(selectedCustomer.value.id)
    if (detail) {
      selectedCustomer.value = { ...mapCustomerFromApi(detail) }
    }
  } catch (error) {
    ElMessage.error(error?.message || '操作失败')
  } finally {
    isCompanySubmitting.value = false
  }
}

const handlePageChange = (page) => {
  if (page < 1 || page > pagination.pages || page === pagination.page) return
  pagination.page = page
  loadCustomers()
}

const handleSearch = () => {
  pagination.page = 1
  loadCustomers()
}

const handleReset = () => {
  searchKeyword.value = ''
  tagKeyword.value = ''
  selectedSource.value = ''
  selectedLevel.value = ''
  selectedSalespersonId.value = null
  salespersonSearchKeyword.value = ''
  pagination.page = 1
  loadCustomers()
}

const handlePageSizeChange = (size) => {
  pagination.size = size
  pagination.page = 1
  loadCustomers()
}

// Computed
const filteredCustomers = computed(() => {
  return customers.value
})

const tagColorClasses = [
  'bg-amber-100 text-amber-800 border-amber-300',
  'bg-rose-100 text-rose-800 border-rose-300',
  'bg-emerald-100 text-emerald-800 border-emerald-300',
  'bg-sky-100 text-sky-800 border-sky-300',
  'bg-indigo-100 text-indigo-800 border-indigo-300',
  'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
  'bg-lime-100 text-lime-800 border-lime-300',
  'bg-orange-100 text-orange-800 border-orange-300'
]

const tagColorMap = new Map()

const getTagClass = (tag) => {
  if (!tag) return tagColorClasses[0]
  if (tagColorMap.has(tag)) {
    return tagColorMap.get(tag)
  }
  const index = Math.floor(Math.random() * tagColorClasses.length)
  const colorClass = tagColorClasses[index]
  tagColorMap.set(tag, colorClass)
  return colorClass
}

const toggleTag = (tag) => {
  const index = customerForm.tags.indexOf(tag)
  if (index > -1) {
    customerForm.tags.splice(index, 1)
  } else {
    if (customerForm.tags.length >= 10) {
      ElMessage.warning('最多只能添加10个标签')
      return
    }
    if (tag.length > 20) {
      ElMessage.warning('单个标签最多20个字')
      return
    }
    customerForm.tags.push(tag)
  }
}

const addTag = () => {
  const value = newTag.value.trim()
  if (!value) return
  if (value.length > 20) {
    ElMessage.warning('单个标签最多20个字')
    return
  }
  if (customerForm.tags.length >= 10) {
    ElMessage.warning('最多只能添加10个标签')
    return
  }
  if (!customerForm.tags.includes(value)) {
    customerForm.tags.push(value)
  }
  newTag.value = ''
}

const openCreateModal = () => {
  isEdit.value = false
  Object.assign(customerForm, {
    id: null,
    name: '', type: '个人', contact: '', gender: '', phone: '', altPhone: '', idCard: '',
    email: '', source: defaultCustomerSource, referrerId: null, level: '普通', status: 0,
    province: '', city: '', address: '', tags: [], remark: '',
    contacts: [{ name: '', phone: '', role: '' }],
    companyName: '', creditCode: '', legalPerson: '', companyNature: '',
    registeredCapital: '', establishedDate: '', certificationDate: '',
    companyAddress: '', businessScope: ''
  })
  referrerSearchKeyword.value = ''
  newTag.value = ''
  showCreateModal.value = true
}

const openEditModal = async (customer) => {
  isEdit.value = true
  selectedCustomer.value = customer
  const detail = await fetchCustomerDetail(customer?.id)
  if (detail) {
    mapCustomerToForm(detail)
  } else {
    mapCustomerToForm(customer || {})
  }
  const referrer = await fetchReferrerById(customerForm.referrerId)
  if (referrer !== undefined) {
    fillReferrerToForm(referrer)
  }
  newTag.value = ''
  showCreateModal.value = true
}

const handleEditCustomerFromDetail = async (customer) => {
  showDetailModal.value = false
  await openEditModal(customer)
}

const closeCreateModal = () => {
  showCreateModal.value = false
}

const openBindReferrerModal = (customer) => {
  referrerBindingCustomer.value = customer
  customerForm.referrerId = null
  referrerSearchKeyword.value = ''
  showBindReferrerModal.value = true
}

const closeBindReferrerModal = () => {
  showBindReferrerModal.value = false
  referrerBindingCustomer.value = null
  customerForm.referrerId = null
  referrerSearchKeyword.value = ''
}

const handleBindReferrer = async () => {
  if (!referrerBindingCustomer.value?.id) return
  if (!customerForm.referrerId) {
    ElMessage.warning('请选择引入人')
    return
  }
  isBindingReferrer.value = true
  try {
    const customer = referrerBindingCustomer.value
    const result = await bindCustomerReferrer(customer.id, {
      customerId: customer.id,
      referrerId: customerForm.referrerId
    })
    ElMessage.success(isApprovalInProgress(result) ? '已提交引入人变更审批，审批通过后生效' : '绑定成功')
    closeBindReferrerModal()
    await loadCustomers()
  } catch (error) {
    ElMessage.error(error?.message || '绑定失败')
  } finally {
    isBindingReferrer.value = false
  }
}

const openDetailModal = async (customer) => {
  selectedCustomer.value = customer
  activeDetailTab.value = 'basic'
  showDetailModal.value = true
  companyPagination.page = 1
  companyPagination.total = 0
  companyPagination.pages = 0
  relatedEnterprises.value = []
  const detail = await fetchCustomerDetail(customer?.id)
  if (detail) {
    selectedCustomer.value = mapCustomerFromApi(detail)
  }
}

const openReferrerDetail = (customer) => {
  selectedReferrerId.value = customer.referrerId
  selectedReferrerInfo.value = {
    referrerName: customer.referrerName,
    name: customer.referrerName,
    phone: customer.phone
  }
  showReferrerDetailModal.value = true
}

const openAddEnterpriseModal = () => {
  if (!selectedCustomer.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  isCompanyEdit.value = false
  resetCompanyForm()
  showCompanyModal.value = true
}

const openEditEnterpriseModal = (company) => {
  isCompanyEdit.value = true
  mapCompanyToForm(company || {})
  showCompanyModal.value = true
}

const closeCompanyModal = () => {
  showCompanyModal.value = false
}

const handleDeleteCompany = async (company) => {
  if (!company?.id) return
  companyToDelete.value = company
  showDeleteCompanyModal.value = true
}

const confirmDeleteCompany = async () => {
  const target = companyToDelete.value
  if (!target?.id) return
  try {
    await deleteCompany(target.id)
    ElMessage.success('删除成功')
    showDeleteCompanyModal.value = false
    companyToDelete.value = null
    await loadCompanies()
    // 更新客户对象触发子组件刷新
    const detail = await fetchCustomerDetail(selectedCustomer.value.id)
    if (detail) {
      selectedCustomer.value = { ...mapCustomerFromApi(detail) }
    }
  } catch (error) {
    ElMessage.error(error?.message || '删除失败')
  }
}

const closeDeleteCompanyModal = () => {
  showDeleteCompanyModal.value = false
  companyToDelete.value = null
}

watch([searchKeyword, tagKeyword, selectedSource, selectedLevel, selectedSalespersonId], () => {
  pagination.page = 1
  loadCustomers()
})

watch([activeDetailTab, () => selectedCustomer.value?.id], ([tab]) => {
  if (tab === 'enterprises') {
    companyPagination.page = 1
    loadCompanies()
    return
  }
  if (tab === 'documents') {
    loadMaterialTypes()
    loadCustomerMaterials()
    return
  }
})

onMounted(() => {
  loadCustomers()
})
</script>
